"use client";

import { ChevronRight, Clock } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { BrandMark } from "@/components/ui/design-system/brand-mark";
import { Hint, Label } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import type { CreatorPortalAccess } from "../creatorAuthContext";
import { formatUsd } from "../formatUsd";
import { ROW_PRESS } from "../one/chrome";
import { requestGuaranteeUsd } from "../requestGuarantee";
import { RequestCover } from "./RequestCover";
import { useRequestCopy } from "./useRequestCopy";
import { type CreatorFeedItem, requestHref } from "./utils";

/**
 * One request in a hairline list: its cover, who it's for, the title, the
 * guarantee and the time left, and the chevron every row in the portal
 * ends in. The guarantee's terms are not repeated here: the lede above the
 * list says them once.
 */
export function RequestRow({
  item,
  now,
  access,
}: {
  item: CreatorFeedItem;
  now: Date;
  access: CreatorPortalAccess | null;
}) {
  const t = useCreatorTranslations("requests");
  const copy = useRequestCopy();
  return (
    <li>
      <Link
        href={requestHref(item, access)}
        className={cn(
          "flex items-center gap-nf-ds-3 rounded-nf-ds-md px-nf-ds-3 py-nf-ds-3 outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nf-ds-brand",
          ROW_PRESS,
        )}
      >
        <RequestCover item={item} className="w-nf-ds-16" />
        <span className="flex min-w-0 flex-1 flex-col gap-nf-ds-1">
          <span className="flex min-w-0 items-center gap-nf-ds-2">
            <BrandMark
              name={item.orgName}
              imageUrl={item.orgLogoUrl}
              className="size-4 rounded-full"
              fallbackClassName="bg-nf-ds-brand text-nf-ds-rail tracking-normal"
            />
            <Hint as="span" muted className="truncate">
              {item.orgName}
            </Hint>
          </span>
          <Label className="line-clamp-2">{item.title}</Label>
          <span className="flex flex-wrap items-baseline gap-x-nf-ds-3 gap-y-nf-ds-1 font-nf-ds-sans text-nf-ds-xs text-muted-foreground">
            <span>
              {t.rich("guarantee.figure", {
                amount: formatUsd(requestGuaranteeUsd(item)),
                figure: (chunks: ReactNode) => (
                  <span className="text-nf-ds-md font-[600] tabular-nums text-foreground">
                    {chunks}
                  </span>
                ),
              })}
            </span>
            <span className="flex items-center gap-nf-ds-1 tabular-nums">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
              {copy.timeLeft(item.expiresAt, now)}
            </span>
          </span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className="size-[18px] shrink-0 text-muted-foreground"
          strokeWidth={2}
        />
      </Link>
    </li>
  );
}
